import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import type { ClientConfig } from '@ferro/config';
import {
  ApiError,
  checkTenantSlugAvailability,
  readSession,
} from '../../../lib/api.js';
import {
  describeSlugError,
  suggestSlugFromDisplayName,
  validateTenantSlug,
} from '../../../lib/tenant-slug.js';

/**
 * Step 1 of the tenant provisioning wizard (D1.5.2).
 *
 * Fields per design §8: legalName, displayName, slug (auto-suggest from
 * displayName, live uniqueness), vertical (radio, default pet-services),
 * contractSignedAt.
 *
 * Slug auto-suggest is sticky-but-overridable: while the admin hasn't
 * manually edited the slug, every keystroke in displayName re-derives it.
 * Once they type into the slug field, suggestions stop until they clear it.
 *
 * Live uniqueness fires on slug blur (after regex passes) and is debounced
 * via the `slugCheckSeq` ref so a stale fetch can't overwrite a newer
 * verdict.
 */

export type BasicInfoVertical = 'pet-services';

export const VERTICAL_OPTIONS: ReadonlyArray<{
  id: BasicInfoVertical;
  label: string;
  description: string;
}> = [
  {
    id: 'pet-services',
    label: 'Pet services',
    description: 'Daycare, boarding, grooming, walking. Default vertical for v1.',
  },
];

export type StepBasicInfoPayload = {
  legalName?: string;
  displayName?: string;
  slug?: string;
  slugTouched?: boolean;
  vertical?: BasicInfoVertical;
  contractSignedAt?: string;
};

type SlugStatus =
  | { kind: 'idle' }
  | { kind: 'invalid'; message: string }
  | { kind: 'checking' }
  | { kind: 'available' }
  | { kind: 'taken' }
  | { kind: 'error'; message: string };

type StepBasicInfoProps = {
  payload: Record<string, unknown>;
  updatePayload: (patch: Record<string, unknown>) => void;
  config: ClientConfig;
};

function readBasicInfo(payload: Record<string, unknown>): StepBasicInfoPayload {
  const legalName = typeof payload.legalName === 'string' ? payload.legalName : '';
  const displayName = typeof payload.displayName === 'string' ? payload.displayName : '';
  const slug = typeof payload.slug === 'string' ? payload.slug : '';
  const slugTouched = payload.slugTouched === true;
  const verticalRaw = typeof payload.vertical === 'string' ? payload.vertical : 'pet-services';
  const vertical: BasicInfoVertical =
    verticalRaw === 'pet-services' ? 'pet-services' : 'pet-services';
  const contractSignedAt =
    typeof payload.contractSignedAt === 'string' ? payload.contractSignedAt : '';
  return { legalName, displayName, slug, slugTouched, vertical, contractSignedAt };
}

export function StepBasicInfo({
  payload,
  updatePayload,
  config,
}: StepBasicInfoProps): JSX.Element {
  const value = useMemo(() => readBasicInfo(payload), [payload]);
  const [slugStatus, setSlugStatus] = useState<SlugStatus>({ kind: 'idle' });
  const slugCheckSeq = useRef(0);

  // Persist the default vertical on first mount so the draft round-trips
  // even if the admin never touches the radio.
  useEffect(() => {
    if (typeof payload.vertical !== 'string') {
      updatePayload({ vertical: 'pet-services' });
    }
    // Run once on mount; payload.vertical is read from the freshest payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-suggest slug from displayName whenever displayName changes — but
  // only if the admin hasn't manually edited the slug yet.
  useEffect(() => {
    if (value.slugTouched) return;
    const suggested = suggestSlugFromDisplayName(value.displayName ?? '');
    if (suggested !== (value.slug ?? '')) {
      updatePayload({ slug: suggested });
    }
    // value.slug is intentionally omitted — we only want to react to displayName.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.displayName, value.slugTouched]);

  // Re-evaluate slug shape on every change so the inline error stays fresh.
  useEffect(() => {
    const slug = value.slug ?? '';
    if (slug.length === 0) {
      setSlugStatus({ kind: 'idle' });
      return;
    }
    const v = validateTenantSlug(slug);
    if (!v.ok) {
      setSlugStatus({ kind: 'invalid', message: describeSlugError(v.error) });
    } else if (slugStatus.kind === 'invalid' || slugStatus.kind === 'idle') {
      setSlugStatus({ kind: 'idle' });
    }
    // We intentionally only depend on the slug string — the status setter
    // and current status are read-only branching guards, not observed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.slug]);

  const onSlugBlur = async (): Promise<void> => {
    const slug = value.slug ?? '';
    const v = validateTenantSlug(slug);
    if (!v.ok) return; // Inline error already showing; no need to call API.
    const seq = ++slugCheckSeq.current;
    setSlugStatus({ kind: 'checking' });
    const session = readSession();
    if (!session.token) {
      setSlugStatus({ kind: 'error', message: 'Sign in to verify slug.' });
      return;
    }
    try {
      const res = await checkTenantSlugAvailability(
        config.deployment.apiUrl,
        session.token,
        v.slug,
      );
      if (slugCheckSeq.current !== seq) return; // Stale response — drop.
      setSlugStatus({ kind: res.available ? 'available' : 'taken' });
    } catch (e) {
      if (slugCheckSeq.current !== seq) return;
      const message =
        e instanceof ApiError
          ? `Couldn't verify slug (${e.status}).`
          : 'Could not reach slug-check service.';
      setSlugStatus({ kind: 'error', message });
    }
  };

  const onLegalName = (e: ChangeEvent<HTMLInputElement>): void => {
    updatePayload({ legalName: e.target.value });
  };

  const onDisplayName = (e: ChangeEvent<HTMLInputElement>): void => {
    updatePayload({ displayName: e.target.value });
  };

  const onSlug = (e: ChangeEvent<HTMLInputElement>): void => {
    const next = e.target.value;
    updatePayload({ slug: next, slugTouched: true });
  };

  const onVertical = (e: ChangeEvent<HTMLInputElement>): void => {
    updatePayload({ vertical: e.target.value });
  };

  const onContract = (e: ChangeEvent<HTMLInputElement>): void => {
    updatePayload({ contractSignedAt: e.target.value });
  };

  const slugInline = describeSlugStatus(slugStatus);

  return (
    <form
      className="ferro-form"
      aria-label="Basic info — Step 1"
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      <label className="ferro-field">
        <span className="ferro-field__label">Legal name</span>
        <input
          type="text"
          name="legalName"
          autoComplete="organization"
          required
          value={value.legalName ?? ''}
          onChange={onLegalName}
          className="ferro-field__input"
          placeholder="Happy Tail Happy Dog LLC"
        />
      </label>

      <label className="ferro-field">
        <span className="ferro-field__label">Display name</span>
        <input
          type="text"
          name="displayName"
          required
          value={value.displayName ?? ''}
          onChange={onDisplayName}
          className="ferro-field__input"
          placeholder="Happy Tail Happy Dog"
        />
      </label>

      <label className="ferro-field">
        <span className="ferro-field__label">
          Slug <span className="ferro-muted">(auto-suggested; editable)</span>
        </span>
        <input
          type="text"
          name="slug"
          required
          inputMode="text"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={value.slug ?? ''}
          onChange={onSlug}
          onBlur={() => {
            void onSlugBlur();
          }}
          className="ferro-field__input"
          placeholder="hthd"
          aria-invalid={
            slugStatus.kind === 'invalid'
            || slugStatus.kind === 'taken'
            || slugStatus.kind === 'error'
              ? true
              : undefined
          }
          aria-describedby="step-basic-info-slug-status"
        />
        {slugInline ? (
          <span
            id="step-basic-info-slug-status"
            className={
              slugStatus.kind === 'available' || slugStatus.kind === 'checking'
                ? 'ferro-muted'
                : 'ferro-form__error'
            }
            role={
              slugStatus.kind === 'invalid'
              || slugStatus.kind === 'taken'
              || slugStatus.kind === 'error'
                ? 'alert'
                : undefined
            }
          >
            {slugInline}
          </span>
        ) : null}
      </label>

      <fieldset className="ferro-field">
        <legend className="ferro-field__label">Vertical</legend>
        {VERTICAL_OPTIONS.map((opt) => (
          <label
            key={opt.id}
            className="ferro-radio"
            style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}
          >
            <input
              type="radio"
              name="vertical"
              value={opt.id}
              checked={(value.vertical ?? 'pet-services') === opt.id}
              onChange={onVertical}
            />
            <span>
              <strong>{opt.label}</strong>{' '}
              <span className="ferro-muted">— {opt.description}</span>
            </span>
          </label>
        ))}
      </fieldset>

      <label className="ferro-field">
        <span className="ferro-field__label">Contract signed</span>
        <input
          type="date"
          name="contractSignedAt"
          value={value.contractSignedAt ?? ''}
          onChange={onContract}
          className="ferro-field__input"
        />
      </label>
    </form>
  );
}

function describeSlugStatus(status: SlugStatus): string | null {
  switch (status.kind) {
    case 'idle':
      return null;
    case 'invalid':
      return status.message;
    case 'checking':
      return 'Checking availability…';
    case 'available':
      return 'Available.';
    case 'taken':
      return 'That slug is already in use. Try another.';
    case 'error':
      return status.message;
  }
}
